import React, { useState, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { LockClosedIcon, ArrowRightIcon, ArrowLeftIcon } from '@radix-ui/react-icons';
import { authService } from '../../services/authService';
import { PasswordInput, Button } from '../../components';
import { AuthLayout } from './AuthLayout';
import { PasswordRequirements } from './PasswordRequirements';
import { PasswordStrength } from './PasswordStrength';
import { isPasswordValid } from '../../utils/passwordValidation';
import { formatApiError, getFieldErrorsFromApi } from '../../utils/errorHandler';
import { ApiError } from '../../types/api';
import { clearAuthCookies } from '../../utils/cookies';
import { useAuthStore } from '../../store/authStore';
import './Auth.scss';

export const SetNewPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token')?.trim() ?? '';

  const hasValidResetLink = token.length > 0;

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showRequirements, setShowRequirements] = useState(false);
  const [passwordError, setPasswordError] = useState<string | undefined>();
  const [confirmError, setConfirmError] = useState<string | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const validate = useCallback((): boolean => {
    let valid = true;

    if (!isPasswordValid(password)) {
      setPasswordError('Password does not meet all requirements');
      valid = false;
    } else {
      setPasswordError(undefined);
    }

    if (!confirmPassword) {
      setConfirmError('Please confirm your password');
      valid = false;
    } else if (password !== confirmPassword) {
      setConfirmError('Passwords do not match');
      valid = false;
    } else {
      setConfirmError(undefined);
    }

    return valid;
  }, [password, confirmPassword]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!hasValidResetLink || !validate()) {
      return;
    }

    setIsLoading(true);
    try {
      await authService.resetPassword({
        token,
        password,
      });
      clearAuthCookies();
      useAuthStore.setState({
        isAuthenticated: false,
        token: null,
        refreshToken: null,
        user: null,
        role: null,
        permissions: [],
        staffUserId: null,
        error: null,
      });
      navigate('/reset-password/success');
    } catch (err) {
      const apiError = err as ApiError;
      const fieldErrors = getFieldErrorsFromApi(apiError);
      if (fieldErrors.password) {
        setPasswordError(fieldErrors.password);
      } else if (fieldErrors.confirmPassword) {
        setConfirmError(fieldErrors.confirmPassword);
      } else {
        setError(formatApiError(apiError) || 'Failed to update password. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!hasValidResetLink) {
    return (
      <AuthLayout>
        <div className="auth-card">
          <h1 className="auth-title">Invalid Reset Link</h1>
          <p className="auth-subtitle">
            This password reset link is missing or malformed. Request a new link to continue.
          </p>
          <Button
            type="button"
            fullWidth
            className="auth-submit-button"
            onClick={() => navigate('/reset-password')}
          >
            Request New Reset Link
            <ArrowRightIcon aria-hidden="true" />
          </Button>
          <Link to="/login" className="auth-back-link">
            <ArrowLeftIcon aria-hidden="true" />
            Back to Login
          </Link>
        </div>
      </AuthLayout>
    );
  }

  const showRequirementsPanel = showRequirements;

  const canSubmit =
    isPasswordValid(password) &&
    confirmPassword.length > 0 &&
    password === confirmPassword &&
    !passwordError &&
    !confirmError;

  const getSubmitDisabledReason = (): string | undefined => {
    if (isLoading) return 'Updating password...';
    if (passwordError) return passwordError;
    if (confirmError) return confirmError;
    if (!isPasswordValid(password)) return 'Password does not meet all requirements';
    if (!confirmPassword) return 'Please confirm your password';
    if (password !== confirmPassword) return 'Passwords do not match';
    return undefined;
  };

  return (
    <AuthLayout tagline="Choose a strong password to secure your admin account.">
      <div className="auth-card">
        <form onSubmit={handleSubmit} className="auth-form" noValidate>
          <h1 className="auth-title">Set New Password</h1>

          <div className="auth-field">
            <label htmlFor="new-password" className="auth-field-label">
              <LockClosedIcon className="auth-field-icon" aria-hidden="true" />
              New Password
            </label>
            <div className="auth-password-field-wrap">
              <PasswordInput
                id="new-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError(null);
                  if (passwordError && isPasswordValid(e.target.value)) {
                    setPasswordError(undefined);
                  }
                  if (confirmPassword && confirmError) {
                    setConfirmError(
                      e.target.value === confirmPassword ? undefined : 'Passwords do not match'
                    );
                  }
                }}
                onFocus={() => setShowRequirements(true)}
                onBlur={() => setShowRequirements(false)}
                error={passwordError}
                autoComplete="new-password"
              />
              {showRequirementsPanel && <PasswordRequirements password={password} />}
            </div>
            <PasswordStrength password={password} />
          </div>

          <div className="auth-field">
            <label htmlFor="confirm-password" className="auth-field-label">
              <LockClosedIcon className="auth-field-icon" aria-hidden="true" />
              Confirm New Password
            </label>
            <PasswordInput
              id="confirm-password"
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setError(null);
                if (!e.target.value) {
                  setConfirmError(undefined);
                } else if (password && e.target.value !== password) {
                  setConfirmError('Passwords do not match');
                } else {
                  setConfirmError(undefined);
                }
              }}
              error={confirmError}
              autoComplete="new-password"
            />
          </div>

          {error && (
            <div className="auth-error" role="alert">
              {error}
            </div>
          )}

          <Button
            type="submit"
            fullWidth
            className="auth-submit-button"
            disabled={isLoading || !canSubmit}
            disabledReason={getSubmitDisabledReason()}
          >
            {isLoading ? 'Updating...' : 'Update Password'}
            {!isLoading && <ArrowRightIcon aria-hidden="true" />}
          </Button>

          <Link to="/login" className="auth-back-link">
            <ArrowLeftIcon aria-hidden="true" />
            Back to Login
          </Link>
        </form>
      </div>
    </AuthLayout>
  );
};
